import { useState } from "react";
import { useT, translateError } from "../i18n/hooks.ts";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  calculateSeriesStats,
  formatSeriesForClipboard,
  insertColumnInSeries,
  parsePastedSeries,
  updateSeriesValue,
  type DailySeries,
} from "../model/dailySeries.ts";
import {
  allScalesFormatDescription,
  detectScale,
  formatScaleDate,
  pasteFormatKey,
  type SeriesScale,
} from "../model/seriesScale.ts";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog.tsx";
import { DuplicateIcon } from "./DuplicateIcon.tsx";
import { EraserIcon } from "./EraserIcon.tsx";
import { InfoIcon } from "./InfoIcon.tsx";
import { LineChartModeIcon } from "./LineChartModeIcon.tsx";
import { RefreshFromFilesIcon } from "./RefreshFromFilesIcon.tsx";
import { SeriesTable } from "./SeriesTable.tsx";
import { TableModeIcon } from "./TableModeIcon.tsx";

const COLOR = "#3fa7b3";

/** What "Delete series" needs to show the right confirmation message before actually deleting anything — see onDeleteSeries below. */
interface PendingDelete {
  sharedWithOthers: boolean;
  confirm: () => void;
}

/**
 * View and paste (from Excel) the series for ONE sub-basin/aquifer inside
 * its own edit dialog — an alternative to uploading the full file from
 * "Input series" (SeriesPanel.tsx) when only one element's data needs
 * fixing or completing. Pasted as two tab-separated columns (date\tvalue,
 * exactly as copied from a spreadsheet); if the project already has a
 * loaded series, the pasted dates must match its own exactly (see
 * insertColumnInSeries, dailySeries.ts — an explicit decision, to avoid
 * silently mixing date ranges).
 */
export function PasteDailySeries({
  title,
  showTitle = true,
  series,
  columnName,
  colIndex,
  scale,
  scaleLocked = true,
  onSeriesChange,
  onIndexChange,
  onDeleteSeries,
}: {
  title: string;
  /** false: doesn't repeat `title` as its own header — for when an external selector already shows it (see EditSubBasinDialog.tsx). */
  showTitle?: boolean;
  series: DailySeries | null;
  columnName: string;
  /** The project's own scale (EvalHidProject.seriesScale) — decides the paste box's expected date format (dd/mm/aaaa or mm/aaaa) and what `calculateSeriesStats` counts as a gap, once `scaleLocked`. While not locked, this is just the not-yet-meaningful default ("daily") — shown in the placeholder as one of the two examples, nothing more. */
  scale: SeriesScale;
  /**
   * Whether the project's scale is already committed (isSeriesScaleLocked,
   * dailySeries.ts) — `true` by default, matching every caller with no real
   * concept of "undecided yet" (e.g. the calibration "Observed" series in
   * OutletPointCatalog.tsx, always parsed against the project's already-real
   * scale). Callers that DO support inferring the scale from the first
   * series ever pasted (EditSubBasinDialog.tsx, EditAquiferDialog) pass the
   * real `isSeriesScaleLocked(series)` instead. While `false`, pasted dates
   * are read tolerantly (either shape) and the real scale is inferred from
   * their own spacing (see `apply()` below) instead of being enforced.
   */
  scaleLocked?: boolean;
  /** Column index (1-based) already persisted on the entity (SubBasin.precipCol/etpCol/tempCol, Aquifer.pumpingCol) — 0: not linked yet. */
  colIndex: number;
  onSeriesChange: (series: DailySeries) => void;
  /**
   * Persists the index of the inserted/updated column on paste — see
   * updateSubBasinColumn/updateAquiferColumn (projectEntities.ts).
   * `inferredScale` is set only when this paste is the one that just
   * established the project's scale (`!scaleLocked` above) — the caller
   * must fold it into the SAME `onChangeProject` call as the index update,
   * not a separate one (two independent project-shaped writes built from
   * the same stale closure would race, same reasoning as SeriesLoader.tsx's
   * own `onEntitiesUpdated`).
   */
  onIndexChange: (index: number, inferredScale?: SeriesScale) => void;
  /**
   * Enables "Delete series" (permanent — see deleteSubBasinSeriesColumn/
   * deleteAquiferPumpingColumn, projectEntities.ts) — absent in contexts with no
   * project to apply it to (e.g. the new-project wizard). Called once, when
   * the trash icon is clicked: computes the result up front (pure, nothing
   * applied yet) so the confirmation dialog can warn if the column turns out
   * to be shared with another sub-basin/aquifer instead of just deleted;
   * `confirm()` is what actually applies it, called if the user confirms.
   */
  onDeleteSeries?: () => PendingDelete;
}) {
  const t = useT();
  const [pasting, setPasting] = useState(false);
  const [pastedText, setPastedText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  const hasColumn = colIndex > 0 && series && colIndex - 1 < series.columns.length;
  const chartData =
    series && hasColumn
      ? series.dates.map((date, i) => ({ date, value: series.values[colIndex - 1][i] }))
      : [];
  const stats =
    series && hasColumn
      ? calculateSeriesStats(series.dates, series.values[colIndex - 1], scale)
      : null;

  function apply() {
    setError(null);
    try {
      const pasted = parsePastedSeries(pastedText, scaleLocked ? scale : undefined);
      // Needs >=2 dates to tell daily from monthly by their actual gap
      // (`detectScale`) — a single date's own shape is ambiguous (e.g.
      // "01/01/2020" alone could be either); with just one row, keep the
      // still-unlocked default ("daily") rather than guess wrong.
      const inferredScale =
        !scaleLocked && pasted.dates.length >= 2 ? detectScale(pasted.dates) : undefined;
      const { series: newSeries, index } = insertColumnInSeries(series, columnName, pasted);
      onSeriesChange(newSeries);
      onIndexChange(index, inferredScale);
      setPastedText("");
      setPasting(false);
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  function cancel() {
    setPasting(false);
    setError(null);
    setPastedText("");
  }

  function copy() {
    if (!series || !hasColumn) return;
    navigator.clipboard.writeText(
      formatSeriesForClipboard(series.dates, series.values[colIndex - 1], scale)
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function changeValue(dateIndex: number, value: number) {
    if (!series) return;
    onSeriesChange(updateSeriesValue(series, colIndex, dateIndex, value));
  }

  return (
    <div className="paste-series">
      {showTitle && (
        <h4>
          {title}
          {!hasColumn && <span className="muted-text"> {t("pasteSeries.noDataYet")}</span>}
        </h4>
      )}

      {pasting ? (
        <div className="step-form">
          <InfoIcon
            text={
              scaleLocked
                ? t("pasteSeries.formatHint", { format: t(pasteFormatKey(scale)) })
                : t("pasteSeries.formatHintUnknownScale", {
                    formats: allScalesFormatDescription(t),
                  })
            }
          />
          <textarea
            className="paste-series-textarea"
            rows={6}
            placeholder={
              scaleLocked && scale === "monthly"
                ? "01/2020\t12.3\n02/2020\t8.1\n…"
                : "01/01/2020\t12.3\n02/01/2020\t8.1\n…"
            }
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
          />
          {error && <p className="error">{error}</p>}
          <div className="paste-series-buttons">
            <button className="button button-primary" onClick={apply} disabled={!pastedText.trim()}>
              {t("common.apply")}
            </button>
            <button className="button" onClick={cancel}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="paste-series-icon-buttons">
          <button
            className="button-icon"
            title={hasColumn ? t("pasteSeries.replaceWithAnother") : t("pasteSeries.pasteSeries")}
            onClick={() => setPasting(true)}
          >
            <RefreshFromFilesIcon />
          </button>
          {hasColumn && (
            <>
              <button
                className="button-icon"
                title={copied ? t("pasteSeries.copied") : t("pasteSeries.copy")}
                onClick={copy}
              >
                <DuplicateIcon />
              </button>
              {onDeleteSeries && (
                <button
                  className="button-icon button-danger"
                  title={t("pasteSeries.deleteSeries")}
                  onClick={() => setPendingDelete(onDeleteSeries())}
                >
                  <EraserIcon />
                </button>
              )}
              <span className="paste-series-view-toggle">
                <button
                  className={viewMode === "chart" ? "button-icon tab-active" : "button-icon"}
                  title={t("pasteSeries.chartView")}
                  onClick={() => setViewMode("chart")}
                >
                  <LineChartModeIcon />
                </button>
                <button
                  className={viewMode === "table" ? "button-icon tab-active" : "button-icon"}
                  title={t("pasteSeries.tableView")}
                  onClick={() => setViewMode("table")}
                >
                  <TableModeIcon />
                </button>
              </span>
            </>
          )}
        </div>
      )}

      {viewMode === "chart" && chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" tickFormatter={(iso: string) => formatScaleDate(iso, scale)} />
            <YAxis width={40} />
            <Tooltip
              labelFormatter={(iso) =>
                typeof iso === "string" ? formatScaleDate(iso, scale) : iso
              }
              formatter={(value) => (typeof value === "number" ? value.toFixed(2) : value)}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={COLOR}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {viewMode === "table" && series && hasColumn && (
        <SeriesTable
          dates={series.dates}
          values={series.values[colIndex - 1]}
          scale={scale}
          onChangeValue={changeValue}
        />
      )}

      {stats && (
        <p className="muted-text paste-series-stats">
          {t(scale === "monthly" ? "pasteSeries.rangeMonthly" : "pasteSeries.rangeDaily", {
            from: formatScaleDate(stats.startDate, scale),
            to: formatScaleDate(stats.endDate, scale),
            n: stats.rowCount,
          })}
          {stats.rowCount > stats.valueCount &&
            t("pasteSeries.noDataSuffix", { n: stats.rowCount - stats.valueCount })}
          {stats.dateGapCount > 0 && t("pasteSeries.gapsSuffix", { n: stats.dateGapCount })}
          <br />
          {t("pasteSeries.average", {
            mean: stats.mean?.toFixed(2) ?? "—",
            max: stats.max?.toFixed(2) ?? "—",
            min: stats.min?.toFixed(2) ?? "—",
          })}
        </p>
      )}

      {pendingDelete && (
        <ConfirmDeleteDialog
          title={t("pasteSeries.confirmDeleteTitle")}
          name={columnName}
          message={
            pendingDelete.sharedWithOthers
              ? t("pasteSeries.confirmUnlinkText", { name: columnName })
              : t("pasteSeries.confirmDeleteText", { name: columnName })
          }
          onConfirm={pendingDelete.confirm}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
